import { isNullOrUndefined } from '@/util/typeGuards';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Button, Paper, Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { FC, useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

type ErrorViewProps = {
  error?: unknown;
  title?: string;
  description?: string;
  onRetry?: () => void;
};

const getErrorMessage = (error: unknown): string => {
  if (isNullOrUndefined(error)) return '';
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return '';
};

export const ErrorView: FC<ErrorViewProps> = ({ error, title, description, onRetry }) => {
  const message = getErrorMessage(error);

  return (
    <Stack
      flex='1 1 100%'
      minHeight='100vh'
      alignItems='center'
      justifyContent='center'
      p={3}
      bgcolor='background.default'
      color='text.primary'
    >
      <Stack alignItems='center' spacing={2} maxWidth={480} textAlign='center'>
        <ErrorOutlineIcon color='error' sx={{ fontSize: 64 }} />
        <Typography variant='h4' component='h1'>
          {title || 'Something went wrong'}
        </Typography>
        <Typography variant='body1' color='text.secondary'>
          {description || 'An unexpected error occurred. Try reloading the page, or go back to the start page.'}
        </Typography>
        {message && (
          <Paper variant='outlined' sx={{ px: 2, py: 1, maxWidth: '100%', overflow: 'auto', bgcolor: 'action.hover' }}>
            <Typography variant='body2' color='text.secondary' fontFamily='monospace'>
              {message}
            </Typography>
          </Paper>
        )}
        <Stack direction='row' spacing={2} pt={1}>
          <Button variant='contained' startIcon={<RefreshIcon />} onClick={onRetry ?? (() => window.location.reload())}>
            Reload page
          </Button>
          <Button variant='outlined' startIcon={<HomeIcon />} href='/'>
            Go to start page
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

type ErrorPageProps = {
  error?: unknown;
  title?: string;
  description?: string;
};

const ErrorPage: FC<ErrorPageProps> = (props) => {
  const routeError = useRouteError();
  const error = props.error ?? routeError;
  const notFound = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (!error) return;

    console.error(error);
    if (!isRouteErrorResponse(error)) {
      Sentry.captureException(error);
    }
  }, [error]);

  return (
    <ErrorView
      error={error}
      title={props.title || (notFound ? 'Page not found' : undefined)}
      description={props.description || (notFound ? 'The page could not be found.' : undefined)}
    />
  );
};

export default ErrorPage;
